import React, { useState } from 'react';
import { BsFileEarmarkX, BsList, BsGrid } from "react-icons/bs";
import { FiChevronRight } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const TestList = ({ setdashboard, currentTests, handleTestClick, selectedTest }) => {
  const [viewMode, setViewMode] = useState('card');

  if (currentTests.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[400px]">
        <BsFileEarmarkX className="text-5xl text-slate-300 mb-4" />
        <h2 className="text-base font-medium text-slate-500">No tests taken yet</h2>
        <p className="text-sm text-slate-400 mt-1 mb-5">Complete a test to see your results here.</p>
        <button
          className="px-5 py-2 text-sm font-medium bg-slate-900 text-white hover:bg-slate-700 transition-all duration-150"
          onClick={() => setdashboard(3)}
        >
          Take a Test
        </button>
      </div>
    );
  }

  const getTestMeta = (test) => {
    const engVal = parseInt(test.eng_score);
    const mathVal = parseInt(test.math_score);
    const hasEng = !isNaN(engVal) && engVal > 0;
    const hasMath = !isNaN(mathVal) && mathVal > 0;

    if (hasEng && hasMath) {
      const score = engVal + mathVal;
      return { score, max: 1600, pct: score / 1600, label: null, kind: 'sat' };
    } else if (hasEng) {
      return { score: engVal, max: 800, pct: engVal / 800, label: 'R&W', kind: 'practice' };
    } else if (hasMath) {
      return { score: mathVal, max: 800, pct: mathVal / 800, label: 'Math', kind: 'practice' };
    }

    try {
      const correct = test.Correct_questionIds ? JSON.parse(test.Correct_questionIds) : [];
      const incorrect = test.Incorrect_questionIds ? JSON.parse(test.Incorrect_questionIds) : [];
      const total = correct.length + incorrect.length;
      const allQ = [...correct, ...incorrect];
      const firstTag = allQ[0]?.tag;
      const label = firstTag === 'math' ? 'Math' : 'R&W';
      const pct = total > 0 ? correct.length / total : 0;
      return { score: correct.length, max: total, pct, label, kind: 'practice', rawCount: true };
    } catch {
      return { score: 0, max: 0, pct: 0, label: null, kind: 'practice' };
    }
  };

  const scoreColor = (pct) =>
    pct >= 0.75 ? 'text-emerald-700' : pct >= 0.5 ? 'text-blue-700' : 'text-rose-600';

  const barColor = (pct) =>
    pct >= 0.75 ? 'bg-emerald-400/70' : pct >= 0.5 ? 'bg-blue-400/70' : 'bg-rose-400/70';

  return (
    <div className="py-4">
      {/* View toggle */}
      <div className="flex items-center justify-end mb-5 gap-1.5">
        <button
          onClick={() => setViewMode('list')}
          title="List view"
          className={`p-2 transition-all duration-150 ${
            viewMode === 'list'
              ? 'bg-slate-900 text-white'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
          }`}
        >
          <BsList size={15} />
        </button>
        <button
          onClick={() => setViewMode('card')}
          title="Card view"
          className={`p-2 transition-all duration-150 ${
            viewMode === 'card'
              ? 'bg-slate-900 text-white'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
          }`}
        >
          <BsGrid size={15} />
        </button>
      </div>

      <AnimatePresence mode="wait">

        {/* ── List view ─────────────────────────────────────────────── */}
        {viewMode === 'list' && (
          <motion.ul
            key="list"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="divide-y divide-slate-100 border border-slate-200"
          >
            {currentTests.map((test, index) => {
              const isSelected = selectedTest?.test_id === test.test_id;
              const { score, max, pct, label, kind } = getTestMeta(test);
              const color = scoreColor(pct);

              return (
                <motion.li
                  key={test.test_id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <button
                    onClick={() => handleTestClick(test)}
                    className={`group w-full flex items-center justify-between px-5 py-3.5 transition-all duration-150
                      ${isSelected
                        ? 'bg-blue-50 border-l-2 border-l-blue-500'
                        : 'bg-white hover:bg-slate-50 border-l-2 border-l-transparent'
                      }`}
                  >
                    {/* Left */}
                    <div className="flex flex-col items-start gap-0.5 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm truncate transition-colors ${isSelected ? 'text-blue-700' : 'text-slate-800'}`}>
                          {test.test_name}
                        </span>
                        {kind === 'practice' && (
                          <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 bg-slate-100 text-slate-500">
                            Practice
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-400">{test.date?.split('T')[0]}</span>
                    </div>

                    {/* Right */}
                    <div className="flex items-center gap-5 shrink-0 ml-4">
                      {kind === 'sat' && (
                        <div className="hidden sm:flex items-center gap-3 text-xs text-slate-400">
                          <span>R&W <span className="font-medium text-slate-600">{test.eng_score || '—'}</span></span>
                          <span className="text-slate-200">|</span>
                          <span>Math <span className="font-medium text-slate-600">{test.math_score || '—'}</span></span>
                        </div>
                      )}
                      <span className={`text-base font-semibold tabular-nums ${color}`}>
                        {kind === 'practice'
                          ? (pct > 0 || score > 0 ? `${Math.round(pct * 100)}%` : '—')
                          : (score > 0 ? score : '—')
                        }
                      </span>
                      <FiChevronRight className={`text-sm transition-colors ${isSelected ? 'text-blue-400' : 'text-slate-300 group-hover:text-slate-500'}`} />
                    </div>
                  </button>
                </motion.li>
              );
            })}
          </motion.ul>
        )}

        {/* ── Card view ─────────────────────────────────────────────── */}
        {viewMode === 'card' && (
          <motion.div
            key="card"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.18 }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {currentTests.map((test, index) => {
              const isSelected = selectedTest?.test_id === test.test_id;
              const { score, max, pct, label, kind, rawCount } = getTestMeta(test);
              const color = scoreColor(pct);
              const bar = barColor(pct);

              return (
                <motion.button
                  key={test.test_id}
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => handleTestClick(test)}
                  className={`relative text-left p-5 border transition-all duration-150
                    ${isSelected
                      ? 'bg-blue-50 border-blue-300 shadow-sm'
                      : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                >
                  {/* Date + type badge */}
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-[11px] text-slate-400">{test.date?.split('T')[0]}</span>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 ${
                      kind === 'practice'
                        ? 'bg-slate-100 text-slate-500'
                        : 'bg-blue-50 text-blue-500'
                    }`}>
                      {kind === 'practice' ? 'Practice' : 'SAT'}
                    </span>
                  </div>

                  {/* Test name — bold, single line, truncated */}
                  <p className="text-sm font-bold text-slate-800 mb-4 truncate" title={test.test_name}>
                    {test.test_name}
                  </p>

                  {/* Score row */}
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1">
                        {kind === 'practice' ? (label || 'Score') : 'Total'}
                      </p>
                      <div className="flex items-baseline gap-1.5">
                        <span className={`text-2xl font-bold tabular-nums ${color}`}>
                          {kind === 'practice'
                            ? (pct > 0 || score > 0 ? `${Math.round(pct * 100)}%` : '—')
                            : (score > 0 ? score : '—')
                          }
                        </span>
                        {kind === 'sat' && score > 0 && (
                          <span className="text-xs text-slate-400">/ {max}</span>
                        )}
                        {kind === 'practice' && rawCount && max > 0 && (
                          <span className="text-xs text-slate-400">{score}/{max}</span>
                        )}
                      </div>
                    </div>

                    {kind === 'sat' && (
                      <div className="text-right space-y-0.5">
                        <p className="text-xs text-slate-500">
                          <span className="text-slate-400 mr-1">R&W</span>
                          <span className="font-semibold">{test.eng_score || '—'}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                          <span className="text-slate-400 mr-1">Math</span>
                          <span className="font-semibold">{test.math_score || '—'}</span>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-1 bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${bar} transition-all duration-700`}
                      style={{ width: score > 0 ? `${Math.min(pct * 100, 100)}%` : '0%' }}
                    />
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  );
};

export default TestList;
